<template>
  <div>
    <table-header
      search
      search-placeholder="Cari berdasarkan nama"
      search-action="products/getProducts"
      :actions="['buttonAdd', 'buttonMore']"
    >
      <template #buttonAdd>
        <b-button
          v-if="canAccess('manage', 'Product')"
          variant="primary"
          class="text-nowrap"
          :to="{ name: 'products-create'}"
        >
          + Tambah Produk
        </b-button>
      </template>
      <template #buttonMore>
        <table-action
          :actions="tableHeaderAction"
          :data="{}"
        />
      </template>
    </table-header>

    <b-skeleton-table
      v-if="isLoading"
      :rows="5"
      :columns="5"
    />
    <b-table
      v-else
      ref="refInvoiceListTable"
      :items="products.data"
      responsive
      :fields="tableFields"
      primary-key="id"
      show-empty
      empty-text="No matching records found"
      class="position-relative mh-160"
    >

      <!-- Column: Product -->
      <template #cell(product)="data">
        <b-media vertical-align="center">
          <template #aside>
            <b-img
              rounded
              width="36"
              class="border"
              :src="linkStaging(`${data.item.images.length ? data.item.images[0].image_url : ''}`, 36, 36)"
              :alt="data.item.name"
              @error="(event) => {
                event.target.src = require('@/assets/images/icons/broken-image.svg')
              }"
            />
          </template>
          <span class="font-weight-bold d-block">
            {{ data.item.name }}
          </span>
          <small class="text-muted">{{ data.item.category.name }}</small>
        </b-media>
      </template>

      <!-- Column: Merchant -->
      <template #cell(merchant)="data">
        <a
          href="#"
          @click.prevent="applyBrandFilter(merchantName(data.item.merchant))"
        >
          {{ merchantName(data.item.merchant) }}
        </a>
      </template>

      <!-- Column: Price -->
      <template #cell(variants[0].originalPrice)="data">
        <span class="text-nowrap">
          {{ toCurrency(data.item.variants[0] ? data.item.variants[0].originalPrice : 0) }}
        </span>
      </template>

      <!-- Column: Price -->
      <template #cell(variants[0].discountedPrice)="data">
        <span class="text-nowrap">
          <span v-if="data.item.variants[0] && data.item.variants[0].discountedPrice">{{ toCurrency(data.item.variants[0].discountedPrice) }}</span>
          <i
            v-else
            class="text-muted"
          >•</i>
        </span>
      </template>

      <!-- Column: Status -->
      <template #cell(status)="data">
        <b-badge
          pill
          :variant="`light-${statusedProduct(data.item.is_active).type}`"
        >
          {{ statusedProduct(data.item.is_active).text }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(stock)="data">
        <div
          class="d-flex align-items-center text-nowrap"
        >
          <span>{{ data.item.variants.reduce((partialSum, item) => parseInt(partialSum) + parseInt(item.old_stock || item.stock), 0) }}</span>
        </div>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div
          v-if="canAccess('manage', 'Product')"
          class="text-nowrap"
        >
          <b-button
            v-if="canAccess('manage', 'Product')"
            size="sm"
            variant="flat-warning"
            class="mr-1 px-50"
            @click="data.toggleDetails"
          >
            <feather-icon
              icon="EditIcon"
            />
            Ubah
          </b-button>
          <table-action
            :actions="tableAction"
            :data="data"
          />
        </div>
      </template>

      <template #row-details="row">
        <b-overlay
          :show="loadingRowDetailId === row.item.id"
          rounded="sm"
        >
          <b-card
            class="border mb-0 bg-light"
            body-class="py-50"
          >
            <b-row>
              <b-col
                cols="auto"
                :style="`width: ${610}px`"
                class="font-weight-bold text-secondary"
              >
                Variant
              </b-col>
              <b-col
                v-b-tooltip.hover.top="{title: 'Ubah sekaligus', disabled: row.item.variants.length === 1}"
                cols="auto"
                :style="`width: ${150}px`"
                class="font-weight-bold text-secondary"
              >
                <div>Harga Awal</div>
                <b-input-group
                  v-if="row.item.variants.length > 1"
                  class="input-group-merge mt-50 mb-50 bulk-input"
                >
                  <b-input-group-prepend>
                    <div class="input-group-text px-75 pr-50">
                      Rp
                    </div>
                  </b-input-group-prepend>
                  <formatted-input
                    v-model="row.item.originalPrice_bulk"
                    type="numeral"
                    :disabled="!row.item.variants.some(variant => variant.can_edit_price)"
                    @input="changeAllPrice(row.index, row.item)"
                    @keydown.enter.prevent.stop="submitMultipleStock(row.item)"
                  />
                </b-input-group>
              </b-col>
              <b-col
                v-b-tooltip.hover.top="{title: 'Ubah sekaligus', disabled: row.item.variants.length === 1}"
                cols="auto"
                :style="`width: ${150}px`"
                class="font-weight-bold text-secondary"
              >
                <div>Harga Diskon</div>
                <b-input-group
                  v-if="row.item.variants.length > 1"
                  class="input-group-merge mt-50 mb-50 bulk-input"
                >
                  <b-input-group-prepend>
                    <div class="input-group-text px-75 pr-50">
                      Rp
                    </div>
                  </b-input-group-prepend>
                  <formatted-input
                    v-model="row.item.discountedPrice_bulk"
                    type="numeral"
                    :disabled="!row.item.variants.some(variant => variant.can_edit_price)"
                    @input="changeAllOldPrice(row.index, row.item)"
                    @keydown.enter.prevent.stop="submitMultipleStock(row.item)"
                  />
                </b-input-group>
              </b-col>
              <b-col
                v-b-tooltip.hover.top="{title: 'Ubah sekaligus', disabled: row.item.variants.length === 1}"
                cols="auto"
                :style="`width: ${100}px`"
                class="font-weight-bold text-secondary"
              >
                <div>Stok</div>
                <b-input-group
                  v-if="row.item.variants.length > 1"
                  class="input-group-merge mt-50 mb-50 bulk-input"
                >
                  <formatted-input
                    v-model="row.item.stock_bulk"
                    type="numeral"
                    @input="changeAllStock(row.index, row.item)"
                    @keydown.enter.prevent.stop="submitMultipleStock(row.item)"
                  />
                </b-input-group>
              </b-col>
              <b-col
                v-if="isProductDirty(row.item)"
                cols="auto"
                :style="`width: ${105}px;align-content:end`"
              >
                <b-button
                  variant="primary"
                  size="sm"
                  class="mb-50 d-inline-flex align-items-center px-75"
                  @click="submitMultipleStock(row.item)"
                >
                  Simpan
                  <span class="save-shortcut-indicator ml-50">
                    <feather-icon
                      icon="CornerDownLeftIcon"
                      size="12"
                    />
                  </span>
                </b-button>
              </b-col>
            </b-row>
            <b-row
              v-for="(item, index) in row.item.variants"
              :key="`variants-${index}`"
              class="py-50"
              align-v="start"
              :class="{ 'border-top': row.item.variants.length > 1, 'mt-1': row.item.variants.length === 1 }"
            >
              <b-col
                cols="auto"
                :style="`width: ${610}px`"
                class="font-weight-bolder"
              >
                <div v-if="item.details.length">
                  <span
                    v-for="(detail, itemIndex) in item.details"
                    :key="`variant-details-${itemIndex}`"
                  ><span v-if="itemIndex > 0"> - </span>{{ detail.value }}</span>
                  <span
                    v-if="item.default_sku"
                    class="font-weight-normal"
                  > (Utama)</span>
                </div>
                <div v-else>
                  (Varian Utama)
                </div>
                <small
                  v-if="item.barcode"
                  v-b-tooltip.hover.top="'Barcode'"
                  class="s-12 font-weight-normal text-muted"
                >
                  {{ item.barcode }}</small>
              </b-col>
              <b-col
                cols="auto"
                :style="`width: ${150}px`"
              >
                <b-input-group class="input-group-merge">
                  <b-input-group-prepend>
                    <div class="input-group-text px-75 pr-50">
                      Rp
                    </div>
                  </b-input-group-prepend>
                  <formatted-input
                    v-model="item.originalPrice"
                    type="numeral"
                    :disabled="!item.can_edit_price"
                    @focus="ensureVariantBaseline(item)"
                    @beforeinput="beforeChangeStock(item, 'originalPrice', item.originalPrice)"
                    @keydown.enter.prevent.stop="submitMultipleStock(row.item)"
                  />
                </b-input-group>
              </b-col>
              <b-col
                cols="auto"
                :style="`width: ${150}px`"
              >
                <b-input-group
                  class="input-group-merge"
                  :class="{'is-invalid': item.discountedPrice && item.originalPrice && Number(item.discountedPrice) >= Number(item.originalPrice)}"
                >
                  <b-input-group-prepend>
                    <div class="input-group-text px-75 pr-50">
                      Rp
                    </div>
                  </b-input-group-prepend>
                  <formatted-input
                    v-model="item.discountedPrice"
                    type="numeral"
                    :class="{'is-invalid': item.discountedPrice && item.originalPrice && Number(item.discountedPrice) >= Number(item.originalPrice)}"
                    :disabled="!item.can_edit_price"
                    @focus="ensureVariantBaseline(item)"
                    @beforeinput="beforeChangeStock(item, 'discountedPrice', item.discountedPrice)"
                    @keydown.enter.prevent.stop="submitMultipleStock(row.item)"
                  />
                </b-input-group>
                <small
                  v-if="item.discountedPrice && item.originalPrice && Number(item.discountedPrice) >= Number(item.originalPrice)"
                  class="text-danger d-block mt-25"
                >
                  Harga tidak sesuai
                </small>
              </b-col>
              <b-col
                cols="auto"
                :style="`width: ${100}px`"
              >
                <formatted-input
                  v-model="item.stock"
                  type="numeral"
                  @focus="ensureVariantBaseline(item)"
                  @beforeinput="beforeChangeStock(item, 'stock', item.stock)"
                  @keydown.enter.prevent.stop="submitMultipleStock(row.item)"
                />
              </b-col>
            </b-row>

            <b-button
              class="position-absolute position-top-0 position-right-0 mr-25 mt-25 px-50"
              size="sm"
              variant="flat-danger"
              pill
              @click="row.toggleDetails"
            >
              <feather-icon
                icon="XIcon"
              />
            </b-button>
          </b-card>
        </b-overlay>
      </template>
    </b-table>

    <div class="mx-2 mb-2">
      <pagination
        :data="products.meta"
        action="products/getProducts"
      />
    </div>

  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import {
  BTable,
  BMedia,
  BCard,
  BImg,
  BRow,
  BCol,
  BSkeletonTable,
  BBadge,
  VBTooltip,
  BButton,
  BOverlay, BInputGroup, BInputGroupPrepend,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { required } from '@validations'
import TableAction from '@/layouts/components/custom/TableAction.vue'
import Pagination from '@/layouts/components/custom/Pagination.vue'
import TableHeader from '@/layouts/components/custom/TableHeader.vue'
import FormattedInput from '@/components/FormattedInput.vue'

export default {
  components: {
    BInputGroupPrepend,
    BInputGroup,
    BSkeletonTable,
    BTable,
    BMedia,
    BRow,
    BCol,
    BImg,
    BCard,
    BBadge,
    FormattedInput,
    TableAction,
    Pagination,
    TableHeader,
    BButton,
    BOverlay,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      tableFields: [
        { key: 'product', label: 'Produk', thStyle: { minWidth: '400px', maxWidth: '400px', width: '400px' } },
        { key: 'merchant', label: 'Brand', thStyle: { minWidth: '230px', maxWidth: '230px', width: '230px' } },
        { key: 'variants[0].originalPrice', label: 'Harga Awal', thStyle: { minWidth: '150px', maxWidth: '150px', width: '150px' } },
        { key: 'variants[0].discountedPrice', label: 'Harga Diskon', thStyle: { minWidth: '150px', maxWidth: '150px', width: '150px' } },
        { key: 'stock', label: 'Stok', thStyle: { minWidth: '100px', maxWidth: '100px', width: '100px' } },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: '', thStyle: { width: '1%' } },
      ],
      detailProductExternal: '',
      isShowDetailProductExternal: false,
      isEditUrl: false,
      isShowSwitch: false,
      loadingRowDetailId: null,
      price: '',
      comparePrice: '',
      stock: '',
      required,
    }
  },
  computed: {
    ...mapState('products', ['products', 'isLoading']),
    query() {
      return this.$route.query
    },
    tableAction() {
      return [
        {
          access: this.canAccess('manage', 'Product'),
          to: 'products-edit|id:id',
          action: '',
          icon: 'EditIcon',
          title: 'Edit',
        },
      ]
    },
    tableHeaderAction() {
      return [
        {
          access: this.canAccess('manage', 'TransferProduct'),
          to: 'products-switch',
          action: '',
          icon: 'ChevronsRightIcon',
          title: 'Transfer Produk',
        },
      ]
    },
    hasDirtyProducts() {
      return (this.products.data || []).some(product => this.isProductDirty(product))
    },
  },
  async created() {
    if (!this.products.data.length) {
      await this.getProducts(this.query)
    }
  },
  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  methods: {
    ...mapActions('products', ['getProducts']),
    merchantName(merchant) {
      if (!merchant) {
        return ''
      }

      return typeof merchant === 'string' ? merchant : (merchant.name || '')
    },
    async applyBrandFilter(brandName) {
      const keyword = (brandName || '').trim()
      if (!keyword) {
        return
      }

      const query = {
        ...this.$route.query,
        keyword,
        page: 1,
      }

      try {
        await this.$router.push({
          name: 'products',
          query,
        })
      } catch (error) {
        // Ignore duplicate navigation when the same brand is clicked again
      }

      await this.getProducts(query)
    },
    handleBeforeUnload(event) {
      if (!this.hasDirtyProducts) {
        return undefined
      }

      const message = 'Anda memiliki perubahan yang belum disimpan. Apakah Anda yakin ingin keluar?'
      // eslint-disable-next-line no-param-reassign
      event.returnValue = message
      return message
    },
    changeAllPrice(index, item) {
      this.products.data[index].variants.forEach(variant => {
        this.beforeChangeStock(variant, 'originalPrice', variant.originalPrice)
        // eslint-disable-next-line no-param-reassign
        variant.originalPrice = item.originalPrice_bulk
      })
    },
    changeAllOldPrice(index, item) {
      this.products.data[index].variants.forEach(variant => {
        this.beforeChangeStock(variant, 'discountedPrice', variant.discountedPrice)
        // eslint-disable-next-line no-param-reassign
        variant.discountedPrice = item.discountedPrice_bulk
      })
    },
    changeAllStock(index, item) {
      this.products.data[index].variants.forEach(variant => {
        this.beforeChangeStock(variant, 'stock', variant.stock)
        // eslint-disable-next-line no-param-reassign
        variant.stock = item.stock_bulk
      })
    },
    ensureVariantBaseline(variant) {
      ['originalPrice', 'discountedPrice', 'stock'].forEach(key => {
        this.beforeChangeStock(variant, key, variant[key])
      })
    },
    beforeChangeStock(variant, key, previousStock) {
      if (variant[`previous__${key}`] === undefined) {
        this.$set(variant, `previous__${key}`, previousStock)
      }
    },
    isFieldDirty(variant, key) {
      const previous = variant[`previous__${key}`]
      if (previous === undefined) {
        return false
      }

      return String(previous ?? '') !== String(variant[key] ?? '')
    },
    isVariantDirty(variant) {
      return ['originalPrice', 'discountedPrice', 'stock'].some(key => this.isFieldDirty(variant, key))
    },
    isProductDirty(product) {
      return (product.variants || []).some(variant => this.isVariantDirty(variant))
    },
    submitMultipleStock(product) {
      // Validation: Don't submit if there is Discount Price >= Normal Price
      const hasInvalidPrice = product.variants.some(v => v.discountedPrice && v.originalPrice && Number(v.discountedPrice) >= Number(v.originalPrice))
      if (hasInvalidPrice) {
        return Promise.resolve()
      }

      this.loadingRowDetailId = product.id

      const withVariant = product.variants.length && product.variants[0].variant !== ''

      // copied from src/layouts/components/form/ProductForm.vue
      const data = {
        with_variant: withVariant ? 1 : 0,
        id: !withVariant ? product.variants[0].id : undefined,
        sku_name: null,
        originalPrice: !withVariant ? product.variants[0].originalPrice : undefined,
        discountedPrice: !withVariant ? product.variants[0].discountedPrice : undefined,
        weight: !withVariant ? product.variants[0].weight : undefined,
        stock: !withVariant ? product.variants[0].stock : undefined,
        variants: !withVariant ? [] : product.variants,
      }

      return this.$store.dispatch('products/updateProductVariant', {
        data,
        id: product.id,
      })
        .then(() => {
          product.variants.forEach(variant => {
            this.$set(variant, 'previous__originalPrice', variant.originalPrice)
            this.$set(variant, 'previous__discountedPrice', variant.discountedPrice)
            this.$set(variant, 'previous__stock', variant.stock)
          })
          // eslint-disable-next-line no-param-reassign
          product.originalPrice_bulk = undefined
          // eslint-disable-next-line no-param-reassign
          product.discountedPrice_bulk = undefined
          // eslint-disable-next-line no-param-reassign
          product.stock_bulk = undefined
        })
        .finally(() => {
          this.loadingRowDetailId = null
        })
    },
  },
}
</script>

<style lang="scss">
.bulk-input * {
  background-color: rgba(255, 255, 255, 0%);
  transition: background-color 300ms;
}

.bulk-input:hover *,
.bulk-input:focus *,
.bulk-input:focus-within * {
  background-color: rgba(255, 255, 255, 100%);
}

.save-shortcut-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 6px;
  background-color: rgba(255, 255, 255, 20%);
}
</style>
